import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';

import {UpdateUserModel, UserModel, UserNotificationSettings, UserRole} from '../../../../types/control';
import {onClickHandler} from '../../../../utils/onclick';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {useKeyboard} from '../../../../hooks/keyboard';
import {hasAuthority} from '../../../../utils/role';
import {SelectItem} from '../../../../components/Select';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useMessage} from '../../../../hooks/message';
import {useQuestion} from '../../../../hooks/question';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {SettingReadOnlyRowComponent} from '../../../../components/Settings/SettingReadOnlyRowComponent';

const BACK_URL = '/settings/users';

const ROLE_SELECTION_ITEMS: SelectItem<UserRole>[] = [
    {value: 'user', label: 'Kullanıcı'},
    {value: 'admin', label: 'Yönetici'},
    {value: 'service', label: 'Servis'},
    {value: 'developer', label: 'Geliştirici'},
];

type Props = {
    onClick: (approved: boolean) => void;
    name: string;
    allowed: boolean;
};

const NotificationSetting = memo(function NotificationSetting({name, allowed, onClick}: Props) {
    return (
        <div className="line">
            <span>{name}</span>
            <div className="half">
                <div className="half" {...onClickHandler(() => onClick(true))}>
                    <div className={'check-box' + (allowed ? ' checked' : '')}/>
                </div>
                <div className="half" {...onClickHandler(() => onClick(false))}>
                    <div className={'check-box' + (allowed ? '' : ' checked')}/>
                </div>
            </div>
        </div>
    );
});

export const UserEditScreen = memo(function UserEditScreen() {
    const params = useParams<{ id: string; }>();
    const history = useHistory();
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [rpc] = useRPC();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);

    const userList = useSelector(state => state.control.userList);
    const currentUsersRole = useSelector(state => state.ui.role);

    const options = useMemo<SelectItem<UserRole>[]>(
        () => ROLE_SELECTION_ITEMS.filter(m => hasAuthority(currentUsersRole, m.value)),
        [currentUsersRole],
    );

    const [selectedUser, setSelectedUser] = useState<UserModel>(null);
    const [name, setName] = useState('');
    const [role, setRole] = useState<SelectItem<UserRole>>(options[0]);
    const [accountPassword, setAccountPassword] = useState('');
    const [alarmPassword, setAlarmPassword] = useState('');
    const [notificationSettings, setNotificationSettings] = useState<UserNotificationSettings>({
        alarm: true,
        intercom: true,
        bell: true,
        intertext: true,
    });

    const toggleNotificationSelected = useCallback((name: string, allowed: boolean) => {
        setNotificationSettings(notificationSettings => ({...notificationSettings, [name]: allowed}));
    }, []);

    const getValidationErrorMessage = useCallback(() => {
        if (!name || name.length < 3 || name === 'default') {
            return 'Lütfen geçerli bir kullanıcı adı giriniz.';
        }

        if ((!selectedUser && !accountPassword) || (accountPassword && accountPassword.length !== 6)) {
            return 'Giriş şifresi 6 haneli olmalıdır.';
        }

        if ((!selectedUser && !alarmPassword) || (alarmPassword && alarmPassword.length !== 4)) {
            return 'Alarm şifresi 4 haneli olmalıdır.';
        }

        return null;
    }, [accountPassword, alarmPassword, name, selectedUser]);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const deletePressed = useCallback(() => {
        question('Silmek istediğinizden emin misiniz?', () => {
            return rpc.deleteUser(selectedUser.id)
                .then(() => {
                    success('Kullanıcı başarılı bir şekilde silindi.');
                    onBackPressed();
                }).catch(failure);
        });
    }, [failure, onBackPressed, question, rpc, selectedUser, success]);

    const createPressed = useCallback(() => {
        const message = getValidationErrorMessage();
        if (message) {
            failure(message);

            return;
        }

        const user: UpdateUserModel = {
            ...(selectedUser ? selectedUser : {id: null}),
            role: role.value,
            username: name,
            notification_settings: notificationSettings,
            password: accountPassword,
            alarm_password: alarmPassword,
        };

        return rpc.updateUser(user)
            .then(() => {
                success('Kullanıcı başarılı bir şekilde ' + (user.id != null ? 'düzenlendi.' : 'oluşturuldu.'));
                onBackPressed();
            }).catch(failure);
    }, [
        getValidationErrorMessage,
        selectedUser,
        role,
        name,
        notificationSettings,
        accountPassword,
        alarmPassword,
        rpc,
        failure,
        success,
        onBackPressed,
    ]);

    const onFocusPassword = useCallback(() => {
        if (accountPassword === '******') {
            setAccountPassword('');
        }
    }, [accountPassword]);

    const onFocusAlarmPassword = useCallback(() => {
        if (alarmPassword === '****') {
            setAlarmPassword('');
        }
    }, [alarmPassword]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    const buttons = useMemo(() => [
        {
            text: 'SİL',
            className: 'mulberry-wood',
            onClick: deletePressed,
            hidden: !selectedUser,
        },
        {
            text: 'KAYDET',
            className: 'green',
            onClick: createPressed,
        },
        {
            text: 'İPTAL',
            className: 'red',
            onClick: onBackPressed,
        },
    ], [deletePressed, selectedUser, createPressed, onBackPressed]);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const id = params.id;
        const user = userList.find(user => user.id === id);
        if (user == null) {
            return;
        }

        setSelectedUser(user);
        setName(user.username);
        setAccountPassword('******');
        setAlarmPassword('****');
        setRole(options.find(option => option.value === user.role));
        setNotificationSettings(user.notification_settings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='user' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder='Kullanıcı adı yazınız...'
                            label='Kullanıcı Adı'
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            maxLength={6}
                            placeholder='Giriş şifresini giriniz...'
                            label='Giriş Şifresi'
                            value={accountPassword}
                            type="number-password"
                            setValue={setAccountPassword}
                            setActiveInput={setActiveNumpadInput}
                            onFocus={onFocusPassword}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            maxLength={4}
                            placeholder='Alarm şifresini giriniz...'
                            label='Alarm Şifresi'
                            type="number-password"
                            value={alarmPassword}
                            setValue={setAlarmPassword}
                            setActiveInput={setActiveNumpadInput}
                            onFocus={onFocusAlarmPassword}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label='Yetki Seviyesi'
                            options={options}
                            onChange={setRole}
                            value={role}/>
                        {
                            renderIf(selectedUser)(() =>
                                <>
                                    <SettingReadOnlyRowComponent
                                        title='Mobil OS'
                                        value={selectedUser.mobile_os}/>
                                    <SettingReadOnlyRowComponent
                                        title='Push Token'
                                        value={selectedUser.push_token}/>
                                </>,
                            )
                        }
                    </div>
                    <div className="content-wrapper">
                        <div className="table-title">
                            <div className="half">
                                <div className="flex">
                                    <span>Bildirim Ayarları</span>
                                </div>
                            </div>
                            <div className="half">
                                <div className="half">
                                    <span>İZİN VER</span>
                                </div>
                                <div className="half">
                                    <span>İZİN VERME</span>
                                </div>
                            </div>
                        </div>
                        <div className="notification-list">
                            <NotificationSetting
                                name="Alarm"
                                allowed={notificationSettings.alarm}
                                onClick={isApproved => toggleNotificationSelected('alarm', isApproved)}/>
                            <NotificationSetting
                                name="İnterkom"
                                allowed={notificationSettings.intercom}
                                onClick={isApproved => toggleNotificationSelected('intercom', isApproved)}/>
                            <NotificationSetting
                                name="Zil"
                                allowed={notificationSettings.bell}
                                onClick={isApproved => toggleNotificationSelected('bell', isApproved)}/>
                            <NotificationSetting
                                name="İntertext"
                                allowed={notificationSettings.intertext}
                                onClick={isApproved => toggleNotificationSelected('intertext', isApproved)}/>
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
